import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigationType, type Location } from 'react-router-dom';
import Masthead from './components/Navbar';
import { CurrencyProvider } from './lib/currency';
import { LanguageProvider, useT } from './i18n';
import { CollectorContext, type Collector } from './data/store';
import { VenueCollectorContext } from './data/venues';
import CurrencyNote from './components/CurrencyNote';
import SearchOverlay from './components/SearchOverlay';
// Eager: a redirect interstitial must paint instantly, not behind a "Loading…".
import Go from './pages/Go';
import { NAV_DATA } from './data/nav';
import { Category } from './types';
import { lazyPage } from './lib/lazy-page';

// Every page is its own chunk, the four big ones too (Home, EventDetail, ListingPage, SearchPage were ~45% of the
// entry bundle, and a visit uses one of them). Nothing flashes: warmRoute below fetches the visited page's chunk before
// hydration, and the inline script in index.html (vite.config.ts routeChunks) starts that download from the first
// bytes of HTML, in parallel with the entry script. Only the redirect interstitial (Go) stays eager.
const Home = lazyPage(() => import('./pages/Home'));
const EventDetail = lazyPage(() => import('./pages/EventDetail'));
const ListingPage = lazyPage(() => import('./pages/ListingPage'));
const SearchPage = lazyPage(() => import('./pages/SearchPage'));
const MapPage = lazyPage(() => import('./pages/MapPage'));
const DirectoryPage = lazyPage(() => import('./pages/DirectoryPage'));
const BlogList = lazyPage(() => import('./pages/Blog').then((m) => ({ default: m.BlogList })));
const BlogPost = lazyPage(() => import('./pages/Blog').then((m) => ({ default: m.BlogPost })));
const Faq = lazyPage(() => import('./pages/Faq'));
const About = lazyPage(() => import('./pages/About'));
const HowItWorks = lazyPage(() => import('./pages/HowItWorks'));
const WhyTrustUs = lazyPage(() => import('./pages/WhyTrustUs'));
const Contact = lazyPage(() => import('./pages/Contact'));
const Feedback = lazyPage(() => import('./pages/Feedback'));
const Privacy = lazyPage(() => import('./pages/Legal').then((m) => ({ default: m.Privacy })));
const Terms = lazyPage(() => import('./pages/Legal').then((m) => ({ default: m.Terms })));
const VenueGuide = lazyPage(() => import('./pages/VenueGuide'));
const PriceIndex = lazyPage(() => import('./pages/PriceIndex'));
// The data desk under /price-index/*: research, market pages, monthly reports (scripts/gen-insights.mjs).
const BestTime = lazyPage(() => import('./pages/insights/BestTime'));
const PastEvents = lazyPage(() => import('./pages/insights/PastEvents'));
const MarketPage = lazyPage(() => import('./pages/insights/MarketPage'));
const WorldSeries = lazyPage(() => import('./pages/WorldSeries'));
const Tournament = lazyPage(() => import('./pages/Tournament'));
const ReportsHub = lazyPage(() => import('./pages/insights/Reports').then((m) => ({ default: m.ReportsHub })));
const ReportPage = lazyPage(() => import('./pages/insights/Reports').then((m) => ({ default: m.ReportPage })));
const Credits = lazyPage(() => import('./pages/Credits'));
// The buyer guides (src/data/guides.json): one chunk for the index and every guide.
const GuidePage = lazyPage(() => import('./pages/guides/Guide'));
const GuidesHub = lazyPage(() => import('./pages/guides/Guide').then((m) => ({ default: m.GuidesHub })));
// Saved and recently viewed events, kept on the device (src/lib/saved.ts); client-only, noindex.
// The saved list's page: fetched once the browser is idle, but only for a browser that has a list
// (prefetchSaved), so the tap on the heart's counter finds it already there.
const Saved = lazyPage(() => import('./pages/Saved'));
const NotFound = lazy(() => import('./pages/NotFound'));
// DESIGN LAB: the owner's sample page (`vite --mode lab` only). In every other mode the condition is
// a build-time constant: the route, the lazy import and the page's fixtures are not in the bundle,
// and /lab is just another address the shell answers with its not-found page.
const Lab = import.meta.env.MODE === 'lab' ? lazy(() => import('./pages/lab/Lab')) : null;

/** A layout effect runs after React has written the new page and before the browser paints it. The
 *  server paints nothing: the prerender and the edge get useEffect, a no-op there that no React
 *  version warns about. */
const useBeforePaint = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Scroll the window at once, whatever scroll-behavior says. ({top: 0, left: 0} also spares the
 *  browser a layout: that spot can't be out of range.) Browsers older than behavior 'instant'
 *  throw on it: a plain scrollTo is instant there too, now that nothing sets smooth on the page. */
const jumpTo = (top: number) => {
  try {
    window.scrollTo({ top, left: 0, behavior: 'instant' });
  } catch {
    window.scrollTo(0, top);
  }
};

/** The element a #hash names, when it is on the page. */
const anchorOf = (hash: string) => {
  if (hash.length < 2) return null;
  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return null;
  }
};

/** Where the visitor was on each history entry (the router's key + the address), for Back/Forward.
 *  sessionStorage mirrors it (this tab only, the last 50 entries); memory alone does without. */
const SCROLL_KEY = 'tixinfo.scroll.v1';
const entryOf = (l: Location) => `${l.key}|${l.pathname}${l.search}${l.hash}`;
let positions: Map<string, number> | null = null;
const scrollMemory = () => {
  if (!positions) {
    positions = new Map();
    try {
      const saved: unknown = JSON.parse(sessionStorage.getItem(SCROLL_KEY) ?? '[]');
      if (Array.isArray(saved)) for (const [k, y] of saved) if (typeof k === 'string' && typeof y === 'number') positions.set(k, y);
    } catch {
      /* nothing saved, or no storage */
    }
  }
  return positions;
};
const remember = (entry: string, y: number) => {
  const m = scrollMemory();
  m.delete(entry);
  m.set(entry, Math.round(y));
  for (const k of m.keys()) {
    if (m.size <= 50) break;
    m.delete(k);
  }
  try {
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify([...m]));
  } catch {
    /* memory only */
  }
};
/** The place we know for this entry, when the router made it (a bare #jump has no state). */
const knownPlace = (l: Location) => (typeof window.history.state?.idx === 'number' ? scrollMemory().get(entryOf(l)) : undefined);
/** The page is long enough to scroll to y. */
const reaches = (y: number) => document.documentElement.scrollHeight - window.innerHeight >= y - 1;
/** This document was opened by Back/Forward or a reload, not by a link. */
const returning = () => {
  try {
    const t = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type;
    return t === 'back_forward' || t === 'reload';
  } catch {
    return false;
  }
};
/** window.scrollY as of the last scroll event: when a page change's effect runs, the page being
 *  left is already gone (and reading scrollY then would make the browser lay out the new page). */
let lastY = 0;

/**
 * Back/Forward to a page that is still too short for the place the visitor left: the browser
 * restores the position right after the page changes, but only as far as the page then reaches, so
 * a page that asks for its rows again (the search page) would stop at its own end. This finishes
 * the job once the page is long enough. It never fights the visitor: their first touch, wheel or key
 * ends it, and so do 4 seconds.
 */
const settle = (want: number) => {
  const stops = ['pointerdown', 'touchstart', 'wheel', 'keydown'] as const;
  let done = false;
  let timer = 0;
  let watch: ResizeObserver | null = null;
  const stop = () => {
    if (done) return;
    done = true;
    window.clearTimeout(timer);
    watch?.disconnect();
    for (const t of stops) window.removeEventListener(t, stop, true);
  };
  for (const t of stops) window.addEventListener(t, stop, { capture: true, passive: true });
  timer = window.setTimeout(stop, 4000);
  if (typeof ResizeObserver !== 'undefined') {
    watch = new ResizeObserver(() => {
      if (!done && document.documentElement.scrollHeight - window.innerHeight >= want - 1) {
        jumpTo(want);
        stop();
      }
    });
    watch.observe(document.body);
  }
  return stop;
};

/**
 * Scrolling on a page change (2026-10-19). A new page opens at its top at once, before the browser
 * paints it: it never shows up half-way down and never glides (that glide was ~0.5 s of every tap
 * made below the first screen). A link to #something on another page opens there. Back and Forward
 * (and a landing, a reload, a Back from another site) keep the place the browser restores; for a
 * page that is still short at that moment, settle() finishes it. An in-page #link glides to its
 * anchor (index.css .smooth-jump), unless the visitor asked for less motion.
 */
const PageScroll = () => {
  const location = useLocation();
  const action = useNavigationType();
  const prev = useRef<Location | null>(null);
  const stopSettle = useRef<(() => void) | null>(null);

  useBeforePaint(() => {
    const from = prev.current;
    prev.current = location;
    if (!from) {
      // The first render: a landing keeps what the browser did (the top, or its #anchor). A reload
      // or a Back from another site: the browser restores what the page already shows; a page that
      // draws its rows later (the search page) gets the rest from settle().
      const want = returning() ? knownPlace(location) : undefined;
      if (want !== undefined) queueMicrotask(() => {
        if (!reaches(want)) stopSettle.current = settle(want);
      });
      return;
    }
    // StrictMode's second run, and a re-render, see the same entry
    if (from.key === location.key) return;
    stopSettle.current?.();
    stopSettle.current = null;
    remember(entryOf(from), lastY);
    if (action === 'POP') {
      // Back/Forward inside the app. React has just drawn the page inside the popstate event, and
      // the browser restores the position when the event is done.
      const want = knownPlace(location);
      if (want === undefined) return;
      queueMicrotask(() => {
        // after every layout effect of this page (one layout, the one the restore needs anyway)
        if (!reaches(want)) stopSettle.current = settle(want);
        // long enough: there now (the browser lands on the same spot); also right when React could
        // not draw the page inside the event and the browser restored on the old one
        else if (Math.abs(window.scrollY - want) > 2) jumpTo(want);
      });
      return;
    }
    if (from.pathname === location.pathname) {
      // the same page with other filters or another search keeps its place; a new #anchor on it is
      // an in-page jump
      if (location.hash && location.hash !== from.hash) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        anchorOf(location.hash)?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
      }
      return;
    }
    const anchor = anchorOf(location.hash);
    if (anchor) {
      anchor.scrollIntoView();
    } else {
      jumpTo(0);
      lastY = 0;
    }
  }, [location, action]);

  useEffect(() => {
    lastY = window.scrollY;
    const onScroll = () => {
      lastY = window.scrollY;
    };
    // leaving for another site, or a reload: this entry's place, for the way back (above)
    const onHide = () => {
      if (prev.current) remember(entryOf(prev.current), window.scrollY);
    };
    // An in-page #link (the board's "price history", a contents list): the browser's own jump,
    // gliding for this one jump only (index.css: never under prefers-reduced-motion).
    const root = document.documentElement;
    let glide = 0;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href^="#"]');
      if (!a || a.getAttribute('href') === '#') return;
      root.classList.add('smooth-jump');
      window.clearTimeout(glide);
      glide = window.setTimeout(() => root.classList.remove('smooth-jump'), 1500);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', onHide);
    document.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('click', onClick);
      window.clearTimeout(glide);
      root.classList.remove('smooth-jump');
      stopSettle.current?.();
    };
  }, []);

  return null;
};

/** The year of this build (nav.json `updated`, rewritten by every build): the same in the
 *  server render and in the browser, and current as long as the daily build runs. */
const COPYRIGHT_YEAR = NAV_DATA.updated.slice(0, 4);

/** Route-chunk fallback — its own component so useT() runs inside the LanguageProvider. */
const Loading = () => {
  const t = useT();
  return (
    <div className="min-h-[100vh]">
      <p className="text-center text-dim italic py-24">{t('Loading…')}</p>
    </div>
  );
};

/** First tab stop on every page: jumps past the header to <main id="main">. Invisible until focused. */
const SkipLink = () => {
  const t = useT();
  return (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-3 focus:text-sm focus:font-semibold focus:text-primary-foreground"
    >
      {t('Skip to content')}
    </a>
  );
};

const FootLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} className="-my-[3px] inline-block py-2 text-dim transition-colors hover:text-cream">
    {children}
  </Link>
);

/** Everything inside the router — exported so the build-time prerenderer can
 *  wrap it in a StaticRouter (src/entry-server.tsx) while the browser wraps
 *  it in BrowserRouter below. */
/** `collector`: set by the prerender so hooks can record the rows a page used. */
/** [venue slug, name]: seating guides the footer links (each exists: /venue/<slug>/seating-guide). */
const SEATING_CHARTS: [string, string][] = [
  ['madison-square-garden', 'Madison Square Garden'],
  ['sphere', 'Sphere'],
  ['sofi-stadium', 'SoFi Stadium'],
  ['red-rocks-amphitheatre', 'Red Rocks'],
  ['hollywood-bowl', 'Hollywood Bowl'],
  ['lambeau-field', 'Lambeau Field'],
];

/** Site footer — its own component so useT() runs inside the LanguageProvider that AppShell mounts. */
const Footer = ({ dark, onToggleTheme }: { dark: boolean; onToggleTheme: () => void }) => {
  const t = useT();
  return (
  <footer className="mt-24 border-t border-border">
    <div className="shell py-14">
      <div className="mb-11 flex max-w-sm items-start">
        <div>
          <p className="text-[15px] font-semibold tracking-tight">
            Tix<span className="text-amber">Info</span>
          </p>
          <p className="mt-3.5 text-[14px] leading-relaxed text-dim">
            {t('TixInfo is a price-comparison engine for live events. We show real partner-marketplace prices with their fee basis labeled, checked daily. We do not sell tickets and we never show fake urgency.')}
          </p>
        </div>
      </div>
      {/* the column headings break (hyphenated where the language allows): Dutch "ZAALPLATTEGRONDEN" is wider than a 320 px phone's column */}
      <div className="grid grid-cols-2 gap-x-10 gap-y-8 pb-5 text-[14.5px] text-dim md:grid-cols-3 lg:grid-cols-5">
        <div>
          <p className="mb-3.5 hyphens-auto break-words text-[12.5px] font-medium uppercase tracking-wider text-cream/70">{t('Browse')}</p>
          <div className="flex flex-col items-start gap-0.5">
            <FootLink to="/music">{t('Concerts')}</FootLink>
            <FootLink to="/sports">{t('Sports')}</FootLink>
            <FootLink to="/comedy">{t('Comedy')}</FootLink>
            <FootLink to="/theater">{t('Theater')}</FootLink>
            <FootLink to="/this-weekend">{t('This Weekend')}</FootLink>
          </div>
        </div>
        <div>
          <p className="mb-3.5 hyphens-auto break-words text-[12.5px] font-medium uppercase tracking-wider text-cream/70">{t('Top cities')}</p>
          <div className="flex flex-col items-start gap-0.5">
            {NAV_DATA.cities.slice(0, 5).map((c) => (
              <FootLink key={c.slug} to={`/city/${c.slug}`}>{c.name}</FootLink>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-3.5 hyphens-auto break-words text-[12.5px] font-medium uppercase tracking-wider text-cream/70">{t('Directories')}</p>
          <div className="flex flex-col items-start gap-0.5">
            <FootLink to="/artists">{t('Artists')}</FootLink>
            <FootLink to="/teams">{t('Teams')}</FootLink>
            <FootLink to="/venues">{t('Venues')}</FootLink>
            <FootLink to="/map">{t('Event Map')}</FootLink>
            <FootLink to="/price-index">{t('Price Index')}</FootLink>
            <FootLink to="/price-index/best-time-to-buy-tickets">{t('Best time to buy')}</FootLink>
            <FootLink to="/price-index/past-events">{t('Ticket price archive')}</FootLink>
            <FootLink to="/price-index/ticket-price-statistics">{t('Ticket price statistics')}</FootLink>
          </div>
        </div>
        <div>
          {/* The seating guides searchers ask for most (Bing Webmaster keyword and page data, 2026-10-23), one link from
              every page: Google found no page linking any guide (Search Console, 2026-10-30) and crawls only a few a day. */}
          <p className="mb-3.5 hyphens-auto break-words text-[12.5px] font-medium uppercase tracking-wider text-cream/70">{t('Seating charts')}</p>
          <div className="flex flex-col items-start gap-0.5">
            {SEATING_CHARTS.map(([slug, name]) => (
              <FootLink key={slug} to={`/venue/${slug}/seating-guide`}>{name}</FootLink>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-3.5 hyphens-auto break-words text-[12.5px] font-medium uppercase tracking-wider text-cream/70">TixInfo</p>
          <div className="flex flex-col items-start gap-0.5">
            <FootLink to="/about">{t('Our story')}</FootLink>
            <FootLink to="/how-it-works">{t('How it works')}</FootLink>
            <FootLink to="/why-trust-us">{t('Why trust us')}</FootLink>
            <FootLink to="/blog">{t('Blog')}</FootLink>
            <FootLink to="/guides">{t('Buyer guides')}</FootLink>
            <FootLink to="/faq">{t('FAQ')}</FootLink>
            <FootLink to="/privacy">{t('Privacy')}</FootLink>
            <FootLink to="/terms">{t('Terms')}</FootLink>
            <FootLink to="/credits">{t('Photo credits')}</FootLink>
            <FootLink to="/contact">{t('Contact')}</FootLink>
            <FootLink to="/feedback">{t('Feedback')}</FootLink>
            <button
              type="button"
              onClick={onToggleTheme}
              className="inline-block py-[5px] text-left text-dim transition-colors hover:text-cream"
            >
              {dark ? t('Light mode') : t('Dark mode')}
            </button>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 border-t border-border pb-5 pt-7" aria-label={t('TixInfo on social media')}>
        {/* X */}
        <a href="https://x.com/tixinfo" target="_blank" rel="noopener" aria-label="TixInfo on X" className="text-faint hover:text-amber transition-colors">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M18.9 1.2h3.7l-8.1 9.3L24 22.8h-7.5l-5.9-7.7-6.7 7.7H.2l8.7-9.9L-.2 1.2h7.7l5.3 7 6.1-7zm-1.3 19.4h2L6.4 3.3H4.2l13.4 17.3z" />
          </svg>
        </a>
        {/* Instagram */}
        <a href="https://www.instagram.com/tixinfo/" target="_blank" rel="noopener" aria-label="TixInfo on Instagram" className="text-faint hover:text-amber transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <rect x="2.5" y="2.5" width="19" height="19" rx="5.2" />
            <circle cx="12" cy="12" r="4.4" />
            <circle cx="17.6" cy="6.4" r="1.3" fill="currentColor" stroke="none" />
          </svg>
        </a>
        {/* No TikTok link: the account is still @videobeelden, not TixInfo (same reason the
            Organization schema in functions/_middleware.ts leaves it out of sameAs). */}
      </div>
      <p className="max-w-4xl text-[12.5px] leading-relaxed text-dim">
        {t('Independent comparison — you buy on the marketplace itself, under that marketplace’s own terms. We may earn a commission at no extra cost to you; our ranking is never sold.')} © {COPYRIGHT_YEAR} Tixinfo.com · {t('We do not sell tickets.')}
      </p>
    </div>
  </footer>
  );
};

/**
 * The lazy page (and its data) a prerendered address needs, fetched BEFORE hydration (src/main.tsx):
 * see src/lib/lazy-page.ts for why a page that is still downloading when React hydrates is thrown away.
 * null = the address is an eager page (nothing to wait for) or unknown (hydrates as before). Same order
 * as the routes below: the specific /price-index/* pages come before /price-index/:slug.
 */
type Warm = [RegExp, (m: RegExpExecArray) => Promise<unknown>];
const WARM: Warm[] = [
  [/^\/tournament\/([^/]+)$/, (m) => Tournament.preload().then((x) => (x as typeof import('./pages/Tournament')).warm(decodeURIComponent(m[1])))],
  [/^\/price-index\/reports\/([^/]+)$/, (m) => Promise.all([ReportPage.preload(), import('./pages/insights/Reports').then((x) => x.warmReport(decodeURIComponent(m[1])))])],
  [/^\/price-index\/reports$/, () => ReportsHub.preload()],
  [/^\/price-index\/best-time-to-buy-tickets$/, () => BestTime.preload()],
  [/^\/price-index\/past-events$/, () => PastEvents.preload()],
  [/^\/price-index\/world-series$/, () => WorldSeries.preload()],
  [/^\/price-index\/([^/]+)$/, (m) => MarketPage.preload().then((x) => (x as typeof import('./pages/insights/MarketPage')).warm(decodeURIComponent(m[1])))],
  [/^\/price-index$/, () => PriceIndex.preload()],
  [/^\/(artists|teams|venues)$/, () => DirectoryPage.preload()],
  [/^\/venue\/[^/]+\/seating-guide$/, () => VenueGuide.preload()],
  [/^\/guides$/, () => GuidesHub.preload()],
  [/^\/guides\/[^/]+$/, () => GuidePage.preload()],
  [/^\/blog$/, () => BlogList.preload()],
  [/^\/blog\/[^/]+$/, () => BlogPost.preload()],
  [/^\/faq$/, () => Faq.preload()],
  [/^\/about$/, () => About.preload()],
  [/^\/how-it-works$/, () => HowItWorks.preload()],
  [/^\/why-trust-us$/, () => WhyTrustUs.preload()],
  [/^\/contact$/, () => Contact.preload()],
  [/^\/feedback$/, () => Feedback.preload()],
  [/^\/privacy$/, () => Privacy.preload()],
  [/^\/terms$/, () => Terms.preload()],
  [/^\/credits$/, () => Credits.preload()],
  [/^\/saved$/, () => Saved.preload()],
  [/^\/$/, () => Home.preload()],
  [/^\/search$/, () => SearchPage.preload()],
  [/^(?:\/[^/]+)?\/event\/[^/]+$/, () => EventDetail.preload()],
  [/^\/(?:music|sports|comedy|theater|theatre)(?:\/[^/]+)?(?:\/\d+)?$|^\/this-weekend$|^\/(?:artist|venue|team|league|city)\/[^/]+(?:\/[^/]+)?$/, () => ListingPage.preload()],
];
export const warmRoute = (pathname: string): Promise<unknown> | null => {
  const path = pathname.replace(/\/+$/, '') || '/';
  for (const [re, go] of WARM) {
    const m = re.exec(path);
    if (m) return go(m);
  }
  return null;
};

const prefetchSaved = () => {
  try {
    if ((localStorage.getItem('tixinfo.saved.v1') ?? '[]').length > 2) void Saved.preload().catch(() => undefined);
  } catch {
    /* storage blocked: no list to open */
  }
};

export const AppShell = ({ collector = null }: { collector?: Collector | null } = {}) => {
  // WHITE is the default identity; dark mode is one click away (footer).
  // The key is v2 on purpose: the dark-default era wrote 'dark' to
  // 'tixinfo.theme' on every visit, so honoring it would keep every
  // returning visitor in the old default. Only an explicit choice counts.
  // typeof guard: this initializer runs during server-side prerendering too.
  const [dark, setDark] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('tixinfo.theme.v2') === 'dark'
  );
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (fn: () => void, o?: { timeout: number }) => number };
    const run = () => (w.requestIdleCallback ? w.requestIdleCallback(prefetchSaved, { timeout: 5000 }) : window.setTimeout(prefetchSaved, 3000));
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
    return () => window.removeEventListener('load', run);
  }, []);
  // Anything that reads the theme must not differ between the prerendered
  // HTML and the first client render (that mismatch throws the whole tree
  // away) — the footer label waits for this.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('tixinfo.theme.v2', dark ? 'dark' : 'light');
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#101114' : '#eef1f5');
  }, [dark]);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <PageScroll />
      <CollectorContext.Provider value={collector}>
      <VenueCollectorContext.Provider value={collector?.venues ?? null}>
      <LanguageProvider>
      <CurrencyProvider>
      <div className="min-h-screen font-serif bg-house text-cream flex flex-col">
        <SkipLink />
        <Masthead onOpenSearch={openSearch} dark={hydrated && dark} onToggleTheme={() => setDark(!dark)} />
        <CurrencyNote />
        <SearchOverlay open={searchOpen} onClose={closeSearch} />

        <main id="main" tabIndex={-1} className="flex-1 outline-none">
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/music" element={<ListingPage type="Category" category={Category.MUSIC} />} />
              <Route path="/sports" element={<ListingPage type="Category" category={Category.SPORTS} />} />
              <Route path="/comedy" element={<ListingPage type="Category" category={Category.COMEDY} />} />
              <Route path="/theater" element={<ListingPage type="Category" category={Category.THEATER} />} />
              {/* Crawlable pages 2…N of each category (the "More dates" link points here). */}
              <Route path="/music/page/:page" element={<ListingPage type="Category" category={Category.MUSIC} />} />
              <Route path="/sports/page/:page" element={<ListingPage type="Category" category={Category.SPORTS} />} />
              <Route path="/comedy/page/:page" element={<ListingPage type="Category" category={Category.COMEDY} />} />
              <Route path="/theater/page/:page" element={<ListingPage type="Category" category={Category.THEATER} />} />
              <Route path="/theatre" element={<Navigate to="/theater" replace />} />
              <Route path="/this-weekend" element={<ListingPage type="Weekend" />} />
              <Route path="/event/:id" element={<EventDetail />} />
              <Route path="/:slug/event/:id" element={<EventDetail />} />
              <Route path="/artist/:name" element={<ListingPage type="Artist" />} />
              <Route path="/artist/:name/:city" element={<ListingPage type="ArtistCity" />} />
              <Route path="/venue/:name" element={<ListingPage type="Venue" />} />
              <Route path="/venue/:name/seating-guide" element={<VenueGuide />} />
              <Route path="/team/:name" element={<ListingPage type="Team" />} />
              <Route path="/league/:name" element={<ListingPage type="League" />} />
              <Route path="/music/:genre" element={<ListingPage type="Genre" category={Category.MUSIC} />} />
              <Route path="/theater/:genre" element={<ListingPage type="Genre" category={Category.THEATER} />} />
              <Route path="/city/:name" element={<ListingPage type="City" />} />
              <Route path="/city/:name/tonight" element={<ListingPage type="City" window="tonight" />} />
              <Route path="/city/:name/this-weekend" element={<ListingPage type="City" window="weekend" />} />
              <Route path="/artists" element={<DirectoryPage type="artist" />} />
              <Route path="/teams" element={<DirectoryPage type="team" />} />
              <Route path="/venues" element={<DirectoryPage type="venue" />} />
              <Route path="/price-index" element={<PriceIndex />} />
              <Route path="/price-index/best-time-to-buy-tickets" element={<BestTime />} />
              <Route path="/price-index/past-events" element={<PastEvents />} />
              <Route path="/price-index/reports" element={<ReportsHub />} />
              <Route path="/price-index/reports/:month" element={<ReportPage />} />
              <Route path="/price-index/world-series" element={<WorldSeries />} />
              <Route path="/tournament/:id" element={<Tournament />} />
              <Route path="/price-index/:slug" element={<MarketPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/out/:market" element={<Go />} />
              <Route path="/map" element={<MapPage />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:id" element={<BlogPost />} />
              <Route path="/guides" element={<GuidesHub />} />
              <Route path="/guides/:slug" element={<GuidePage />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/about" element={<About />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/why-trust-us" element={<WhyTrustUs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/credits" element={<Credits />} />
              <Route path="/saved" element={<Saved />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>

        <Footer dark={hydrated && dark} onToggleTheme={() => setDark(!dark)} />
      </div>
      </CurrencyProvider>
      </LanguageProvider>
      </VenueCollectorContext.Provider>
      </CollectorContext.Provider>
    </>
  );
};

const App = () => (
  <BrowserRouter>
    {Lab ? (
      <Routes>
        <Route
          path="/lab"
          element={
            <Suspense fallback={null}>
              <Lab />
            </Suspense>
          }
        />
        <Route path="*" element={<AppShell />} />
      </Routes>
    ) : (
      <AppShell />
    )}
  </BrowserRouter>
);

export default App;
